import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Clock, BookOpen, Trash2, Check, Swords, HeartHandshake, GitMerge, User as UserIcon, MapPin, Pencil, CalendarDays, FlagOff } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import TableFormDialog from "@/components/CreateTableDialog";
import GameSummaryDialog from "@/components/GameSummaryDialog";


const MODE_META = {
  Competitivo: { icon: Swords, label: "Competitivo" },
  Collaborativo: { icon: HeartHandshake, label: "Collaborativo" },
  "Semi-collaborativo": { icon: GitMerge, label: "Semi-collab." },
  Solitario: { icon: UserIcon, label: "Solitario" },
};

const GENRE_IMAGES = {
  "Eurogame": "/images/genres/eurogame.png",
  "American": "/images/genres/american.png",
  "Deck Building": "/images/genres/deck-building.png",
  "Dungeon Crawler": "/images/genres/dungeon-crawler.png",
  "Legacy": "/images/genres/legacy.png",
  "Party": "/images/genres/party.png",
  "Family": "/images/genres/family.png",
  "Roll & Write": "/images/genres/roll-and-write.png",
  "Escape Room": "/images/genres/escape-room.png",
  "Astratto": "/images/genres/astratto.png",
  "Wargame": "/images/genres/wargame.png",
};
const FALLBACK_IMAGE = "/images/genres/altro.png";

export default function TableCard({ table, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [summaryOpen, setSummaryOpen] = React.useState(false);

  const isJoined = table.players.some((p) => p.id === user.id);
  const isCreator = table.creator.id === user.id;
  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const canEdit = isCreator || isAdmin;
  const canDelete = isCreator || isAdmin;
  const seatsTaken = table.players.length + (table.extra_players ?? 0);
  const seatsTotal = table.max_players;
  const full = seatsTaken >= seatsTotal;

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/tables/${table.id}/join`);
      toast.success("Iscritto al tavolo!");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await api.post(`/tables/${table.id}/leave`);
      toast.success("Hai lasciato il tavolo");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare questo tavolo?")) return;
    setBusy(true);
    try {
      await api.delete(`/tables/${table.id}`);
      toast.success("Tavolo eliminato");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const genreImage = (table.categories || []).map((c) => GENRE_IMAGES[c]).find(Boolean);
  const coverImage = table.image_url || table.bgg_image || table.bgg_thumbnail || genreImage || FALLBACK_IMAGE;

  return (
    <TooltipProvider delayDuration={200}>
      <article
      data-testid={`table-card-${table.id}`}
      className="bg-white dark:bg-[#111111] border border-[#EAE3D9]/60 dark:border-[#2A2A2A] rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden"
    >
      <div className="h-36 w-full relative bg-gray-100 dark:bg-gray-800">
        {table.bgg_id ? (
          <a
            href={`https://boardgamegeek.com/boardgame/${table.bgg_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full h-full"
            title={`Vedi ${table.game_name} su BGG`}
          >
            <img
              src={coverImage}
              alt={table.game_name}
              className="w-full h-full object-cover hover:brightness-90 transition-[filter] duration-200"
              onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
            />
          </a>
        ) : (
          <img
            src={coverImage}
            alt={table.game_name}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
          />
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1">
          {(table.categories || [table.category]).map((cat) => (
            <Badge key={cat} className="bg-black/60 text-white backdrop-blur-md hover:bg-black/80 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider border-0">
              {cat}
            </Badge>
          ))}
        </div>
        {table.game_mode && (() => {
          const m = MODE_META[table.game_mode] || MODE_META.Competitivo;
          const Icon = m.icon;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md rounded-lg p-1.5 cursor-help">
                  <Icon size={14} className="text-white/90" strokeWidth={2} />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>Modalità: {m.label}</p></TooltipContent>
            </Tooltip>
          );
        })()}
        {(canEdit || canDelete) && (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-md rounded-lg p-1">
            {canEdit && (
              <TableFormDialog
                mode="edit"
                initial={table}
                onSaved={onChanged}
                trigger={
                  <button
                    disabled={busy}
                    data-testid={`edit-table-${table.id}`}
                    className="text-white/80 hover:text-white p-1.5 transition-colors"
                    title="Modifica tavolo"
                  >
                    <Pencil size={14} />
                  </button>
                }
              />
            )}
            {canDelete && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setSummaryOpen(true)}
                      disabled={busy}
                      className="text-white/80 hover:text-yellow-300 p-1.5 transition-colors"
                      title="Chiudi partita"
                    >
                      <FlagOff size={14} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent><p>Chiudi partita e salva nello storico</p></TooltipContent>
                </Tooltip>
                <button
                  onClick={remove}
                  disabled={busy}
                  data-testid={`delete-table-${table.id}`}
                  className="text-white/80 hover:text-red-400 p-1.5 transition-colors"
                  title="Elimina tavolo"
                >
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        )}
        <GameSummaryDialog
          table={table}
          open={summaryOpen}
          onOpenChange={setSummaryOpen}
          onSaved={onChanged}
        />
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <div className="min-w-0">
          <h3 className="font-display text-2xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] leading-tight truncate">
            {table.game_name}
          </h3>
          <p className="text-xs text-[#757169] dark:text-[#B8B2AA] mt-1 mb-2">
            creato da <span className="font-medium text-[#4A4742] dark:text-[#D8D0C6]">{table.creator.name}</span>
          </p>
          {(table.venue || table.game_date) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {table.venue && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#DDE7DC] dark:bg-[#1A2E20] text-[#2B593F] dark:text-[#8FC79F] border border-[#C3D9C5] dark:border-[#2B593F]/40 px-2.5 py-1 rounded-full">
                  <MapPin size={10} strokeWidth={2} />
                  {table.venue}
                </span>
              )}
              {table.game_date && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#DDE6F0] dark:bg-[#1A2535] text-[#2D5C7C] dark:text-[#7BB8D4] border border-[#C3D4E0] dark:border-[#2D5C7C]/40 px-2.5 py-1 rounded-full">
                  <CalendarDays size={10} strokeWidth={2} />
                  {new Date(table.game_date + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                </span>
              )}
            </div>
          )}
        </div>

      <div className="mt-3 h-[2.75rem]">
        {table.notes && (
          <div className="h-full overflow-y-auto rounded-lg border border-[#EAE3D9] dark:border-[#2A2A2A] bg-[#FDFBF7] dark:bg-[#1A1A1A] px-2.5 py-1.5">
            <p className="text-sm text-[#4A4742] dark:text-[#C8C2BA] leading-relaxed">{table.notes}</p>
          </div>
        )}
      </div>

      <div className="flex items-stretch gap-2 mt-4 mb-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <div tabIndex={0} onClick={(e) => e.preventDefault()} className="flex-1 flex flex-col items-center gap-1 bg-[#F5F1EA] dark:bg-[#1A1A1A] rounded-xl py-2 px-1 cursor-help outline-none">
              <Users size={16} className="text-[#2B593F] dark:text-[#8FC79F]" strokeWidth={1.8} />
              <span className="text-[9px] uppercase tracking-widest text-[#A09890] dark:text-[#5A5550] font-medium">Posti</span>
              <span data-testid={`table-seats-${table.id}`} className="text-sm font-bold text-[#2D2A26] dark:text-[#F5F1EA] leading-none">{seatsTaken}/{seatsTotal}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent><p>Posti occupati / totali</p></TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div tabIndex={0} onClick={(e) => e.preventDefault()} className="flex-1 flex flex-col items-center gap-1 bg-[#F5F1EA] dark:bg-[#1A1A1A] rounded-xl py-2 px-1 cursor-help outline-none">
              <BookOpen size={16} className="text-[#2B593F] dark:text-[#8FC79F]" strokeWidth={1.8} />
              <span className="text-[9px] uppercase tracking-widest text-[#A09890] dark:text-[#5A5550] font-medium">Spiega</span>
              <span className="text-sm font-bold text-[#2D2A26] dark:text-[#F5F1EA] leading-none">{table.explanation_time}m</span>
            </div>
          </TooltipTrigger>
          <TooltipContent><p>Tempo di spiegazione</p></TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div tabIndex={0} onClick={(e) => e.preventDefault()} className="flex-1 flex flex-col items-center gap-1 bg-[#F5F1EA] dark:bg-[#1A1A1A] rounded-xl py-2 px-1 cursor-help outline-none">
              <Clock size={16} className="text-[#2B593F] dark:text-[#8FC79F]" strokeWidth={1.8} />
              <span className="text-[9px] uppercase tracking-widest text-[#A09890] dark:text-[#5A5550] font-medium">Gioco</span>
              <span className="text-sm font-bold text-[#2D2A26] dark:text-[#F5F1EA] leading-none">{table.play_time}m</span>
            </div>
          </TooltipTrigger>
          <TooltipContent><p>Durata stimata</p></TooltipContent>
        </Tooltip>

      </div>

      <div className="mt-auto pt-4 flex items-center justify-between">
        <div className="flex -space-x-2">
          {table.players.slice(0, 5).map((p, i) => (
            <Tooltip key={p.id}>
              <TooltipTrigger asChild>
                <div
                  tabIndex={0}
                  onClick={(e) => e.preventDefault()}
                  className="w-8 h-8 rounded-full bg-[#F5F1EA] dark:bg-[#1A1A1A] border-2 border-white dark:border-[#111111] flex items-center justify-center text-xs font-bold text-[#4A4742] dark:text-[#C8C2BA] relative z-10 cursor-help overflow-hidden outline-none"
                  style={{ zIndex: 10 - i }}
                >
                  {p.avatar ? (
                    <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${p.avatar}`} alt={p.name} className="w-full h-full object-cover bg-[#EAE3D9] dark:bg-[#2A2A2A]" />
                  ) : (
                    p.name.charAt(0).toUpperCase()
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent><p>{p.name}</p></TooltipContent>
            </Tooltip>
          ))}
          {table.players.length > 5 && (
            <div className="w-8 h-8 rounded-full bg-[#EAE3D9] dark:bg-[#2A2A2A] border-2 border-white dark:border-[#111111] flex items-center justify-center text-[10px] font-bold text-[#4A4742] dark:text-[#C8C2BA] relative z-0">
              +{table.players.length - 5}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        {isJoined ? (
          <Button
            onClick={leave}
            disabled={busy}
            data-testid={`leave-table-${table.id}`}
            variant="ghost"
            className="w-full h-11 rounded-xl text-[#C8553D] bg-[#F4DAD3]/40 dark:bg-[#C8553D]/10 hover:bg-[#F4DAD3]/70 dark:hover:bg-[#C8553D]/20 font-semibold transition-colors"
          >
            Lascia il tavolo
          </Button>
        ) : (
          <Button
            onClick={join}
            disabled={busy || full}
            data-testid={`join-table-${table.id}`}
            className="w-full h-11 rounded-xl bg-[#2B593F] hover:bg-[#1E3F2C] dark:bg-[#2B593F] dark:hover:bg-[#1E3F2C] text-[#FDFBF7] font-semibold transition-colors shadow-sm disabled:opacity-50"
          >
            {full ? "Tavolo completo" : "Unisciti al tavolo"}
          </Button>
        )}
      </div>
      </div>
      </article>
    </TooltipProvider>
  );
}
